/* ══════════════════════════════════════════════════════════════
   Strategy Detail — Full Strategy Display with Levels & Commentary
   Expanded view for Strategy page
   ══════════════════════════════════════════════════════════════ */

import { fetchMLStrategy } from '../services/ml-api';
import type { MLStrategyResponse } from '../services/ml-api';
import { renderUnavailableBlock } from './data-state';

export interface StrategyDetailData {
  strategy: string;
  label: string;
  confidence: number;
  riskLevel: string;
  entry: { min: number; max: number };
  target: { min: number; max: number };
  stopLoss: number;
  riskReward: number;
  aiCommentary: string;
  sectorsToWatch: Array<{ name: string; sentiment: string; emoji: string }>;
  keyLevels: { support: number[]; resistance: number[] };
  modelVersions: Record<string, string>;
}

export function renderStrategyDetail(container: HTMLElement, data?: StrategyDetailData): void {
  if (data) {
    renderWithData(container, data);
  } else {
    container.innerHTML = `<div style="padding: 2rem; text-align: center;">Loading strategy data...</div>`;
    fetchMLStrategy().then(strat => {
      if (strat) {
        renderWithData(container, mapStrategyResponse(strat));
      } else {
        container.innerHTML = renderUnavailableBlock('glass-card strategy-detail-panel', 'Live strategy unavailable');
      }
    }).catch(() => {
      container.innerHTML = renderUnavailableBlock('glass-card strategy-detail-panel', 'Live strategy unavailable');
    });
  }
}

function mapStrategyResponse(strat: MLStrategyResponse): StrategyDetailData {
  return {
    strategy: strat.strategy,
    label: strat.strategyLabel || strat.strategy?.toUpperCase() || 'MOMENTUM LONG',
    confidence: strat.confidence || 0,
    riskLevel: strat.riskLevel || 'moderate',
    entry: strat.entry,
    target: strat.target,
    stopLoss: strat.stopLoss || 0,
    riskReward: strat.riskReward || 1,
    aiCommentary: strat.description || 'Live strategy signal generated from the deployed ONNX inference path.',
    sectorsToWatch: (strat.sectorsToWatch || []).map((sector) => ({
      ...sector,
      emoji: sector.sentiment === 'bullish' ? '🟢' : sector.sentiment === 'bearish' ? '🔴' : '🟡',
    })),
    keyLevels: strat.keyLevels || { support: [], resistance: [] },
    modelVersions: {
      regime: strat.modelSource || 'rule_based',
      strategy: strat.modelSource || 'rule_based',
      ensemble: 'live-api',
    },
  };
}

function renderWithData(container: HTMLElement, d: StrategyDetailData): void {

  const rr = d.riskReward >= 1.5 ? 'var(--india-green)' : d.riskReward >= 1 ? 'var(--india-saffron)' : 'var(--india-red)';
  const fmt = (n: number) => `₹${n.toLocaleString('en-IN')}`;

  container.innerHTML = `
    <div class="glass-card strategy-detail-panel">
      <div class="card-header">
        <h3>🎯 ${d.label}</h3>
        <div class="strategy-badges">
          <span class="confidence-badge">${d.confidence}% confidence</span>
          <span class="risk-badge risk-${d.riskLevel}">${d.riskLevel.toUpperCase()}</span>
        </div>
      </div>

      <div class="strategy-levels-grid">
        <div class="level-card entry-card">
          <span class="level-icon">🟢</span>
          <span class="level-label">Entry Zone</span>
          <span class="level-value">${fmt(d.entry.min)} - ${fmt(d.entry.max)}</span>
        </div>
        <div class="level-card target-card">
          <span class="level-icon">🎯</span>
          <span class="level-label">Target Zone</span>
          <span class="level-value">${fmt(d.target.min)} - ${fmt(d.target.max)}</span>
        </div>
        <div class="level-card sl-card">
          <span class="level-icon">🛑</span>
          <span class="level-label">Stop Loss</span>
          <span class="level-value">${fmt(d.stopLoss)}</span>
        </div>
        <div class="level-card rr-card">
          <span class="level-icon">⚖️</span>
          <span class="level-label">Risk:Reward</span>
          <span class="level-value" style="color:${rr}">1:${d.riskReward.toFixed(1)}</span>
        </div>
      </div>

      <div class="strategy-commentary">
        <h4>🤖 AI Commentary</h4>
        <div class="commentary-text">${d.aiCommentary.split('\n').map(p => `<p>${p}</p>`).join('')}</div>
      </div>

      <div class="strategy-sectors">
        <h4>📊 Sectors to Watch</h4>
        <div class="sectors-grid">
          ${d.sectorsToWatch.length > 0 ? d.sectorsToWatch.map(s => `
            <div class="sector-chip ${s.sentiment}">
              <span>${s.emoji}</span>
              <span>${s.name}</span>
              <span class="sentiment-dot"></span>
            </div>`).join('') : '<div class="text-muted text-sm">No live sector watchlist available</div>'}
        </div>
      </div>

      <div class="strategy-key-levels">
        <h4>📏 Key Levels</h4>
        <div class="levels-row">
          <div class="support-levels">
            <span class="levels-label">Support</span>
            ${d.keyLevels.support.length > 0 ? d.keyLevels.support.map(l => `<span class="level-chip support">${fmt(l)}</span>`).join('') : '<span class="text-muted text-sm">Unavailable</span>'}
          </div>
          <div class="resistance-levels">
            <span class="levels-label">Resistance</span>
            ${d.keyLevels.resistance.length > 0 ? d.keyLevels.resistance.map(l => `<span class="level-chip resistance">${fmt(l)}</span>`).join('') : '<span class="text-muted text-sm">Unavailable</span>'}
          </div>
        </div>
      </div>

      <div class="strategy-models">
        <small>Models: ${Object.entries(d.modelVersions).map(([k, v]) => `${k}:${v}`).join(' · ')}</small>
      </div>
    </div>`;
}
